import {
	type AttachmentDateFilterValue,
	isAttachmentDateFilterValue,
} from './attachmentDateFilters.tsx';
import { type AttachmentFileType, isAttachmentFileType } from './attachmentFileTypes.tsx';

/** URL query params used to persist the attachment filters. */
export const attachmentFilterUrlParams = {
	/** Persists the "Added by" filter. */
	addedBy: 'addedBy',
	/** Persists the "Attachment type" filter. */
	fileTypes: 'fileTypes',
	/** Persists the "Date added" filter. */
	dateAdded: 'dateAdded',
} as const;

/**
 * A raw query param value. Route matchers can hand back an array when a param
 * is repeated, and `undefined` when it is absent.
 */
export type QueryParamValue = string | string[] | undefined | null;

/**
 * Parses a query param holding a JSON array. Missing or malformed values are
 * treated as "no filter" so callers never have to guard against bad input.
 */
const parseJsonArray = (value: QueryParamValue): unknown[] => {
	if (typeof value !== 'string' || value.trim() === '') {
		return [];
	}
	try {
		const parsed: unknown = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		// Malformed param — treat as no filter.
		return [];
	}
};

/**
 * Serialises a list to the URL param format: ["a","b"]. An empty list clears
 * the param entirely.
 */
const serializeList = (values: readonly string[]): string | undefined =>
	values.length > 0 ? JSON.stringify(values) : undefined;

/**
 * Parses the `addedBy` param into a list of author account ids. Account ids are
 * opaque, so there is no way to tell a bad entry from a good one — a single
 * non-string entry means the param is corrupt and the whole filter is dropped.
 */
export const parseAddedByParam = (value: QueryParamValue): string[] => {
	const entries = parseJsonArray(value);
	const accountIds = entries.filter((entry): entry is string => typeof entry === 'string');
	return accountIds.length === entries.length ? accountIds : [];
};

/** Serialises author account ids for the `addedBy` param. */
export const serializeAddedByParam = (accountIds: readonly string[]): string | undefined =>
	serializeList(accountIds);

/**
 * Parses the `fileTypes` param into file type groupings, dropping any value that
 * is not a known grouping.
 */
export const parseFileTypesParam = (value: QueryParamValue): AttachmentFileType[] =>
	Array.from(new Set(parseJsonArray(value).filter(isAttachmentFileType)));

/** Serialises file type groupings for the `fileTypes` param. */
export const serializeFileTypesParam = (
	fileTypes: readonly AttachmentFileType[],
): string | undefined => serializeList(fileTypes);

/**
 * Parses the `dateAdded` param into a single "Date added" filter value. The
 * filter is single-select, so the param holds one readable value rather than a
 * JSON array. Missing or unknown values are treated as "no filter".
 */
export const parseDateAddedParam = (value: QueryParamValue): AttachmentDateFilterValue | null =>
	isAttachmentDateFilterValue(value) ? value : null;

/** Serialises a "Date added" filter value for the `dateAdded` param. */
export const serializeDateAddedParam = (
	value: AttachmentDateFilterValue | null,
): string | undefined => value ?? undefined;
